import { __assign, __awaiter, __generator, __read } from "tslib";
import { DealAPIContext } from "@shared/contexts/api/DealAPIContext";
import { DealExtractors } from "@shared/models/deal";
import { ROUTES } from "@router/routes";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { ColouredTag } from "@components/common/ColouredTag";
import { DefaultTagGroupDetail } from "../settings/DefaultTagGroupDetail";
import { useSearchPagination } from "@hooks/useSearchPagination";
import { TagContextMenu } from "../settings/TagContextMenu";
import { useNavigation } from "@hooks/useNavigation";
import cn from "classnames";
import { useAppToast } from "../common/Toast";
import { MergeTagDialog, useMergeTagDialog } from "../common/MergeTagDialog";
import { TagGroupSlug } from "@shared/models/tag";
export var DealTagGroupDetailScreen = React.memo(function () {
    var navigation = useNavigation();
    var _a = useAppToast(), showErrorToast = _a.showErrorToast, showSuccessToast = _a.showSuccessToast;
    var _b = __read(useState({}), 2), enableStates = _b[0], setEnableStates = _b[1];
    var dealAPI = useContext(DealAPIContext);
    var onGoBack = useCallback(function () {
        if (!navigation.goBack()) {
            navigation.push(ROUTES.settings.tags.root);
        }
    }, [navigation]);
    var onFetchListItemError = useCallback(function () {
        showErrorToast({
            titleId: "common.errors.data.fetch_failed.title",
            descriptionId: "common.errors.data.fetch_failed.description",
        }, {
            position: "bottom-center",
        });
        onGoBack();
    }, [onGoBack, showErrorToast]);
    var _c = useSearchPagination(dealAPI.listDeals, {
        page: 1,
        perPage: 100,
        isEnabled: "any",
    }, onFetchListItemError), data = _c.data, totalCount = _c.totalCount, isInitializing = _c.isInitializing, isFetchingMore = _c.isFetchingMore, hasMore = _c.hasMore, nextPage = _c.nextPage, search = _c.search, refresh = _c.refresh;
    var onAddNewDeal = useCallback(function () {
        navigation.push(ROUTES.settings.tags.deal.add);
    }, [navigation]);
    var onEditGroup = useCallback(function () {
        navigation.push(ROUTES.settings.tags.deal.edit_group);
    }, [navigation]);
    var onEditTag = useCallback(function (deal) {
        navigation.push("".concat(ROUTES.settings.tags.deal.group_detail, "/").concat(deal.id, "/edit"));
    }, [navigation]);
    var onEnableTag = useCallback(function (deal) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            setEnableStates(function (prevState) {
                var _a;
                return (__assign(__assign({}, prevState), (_a = {}, _a[deal.id] = true, _a)));
            });
            dealAPI
                .enableDeal(deal.id)
                .then(function () {
                showSuccessToast({
                    titleId: "settings.tags.enable_tag.success.title",
                }, {
                    position: "bottom-center",
                });
            })
                .catch(function (err) {
                console.error("Failed to enable tag", err);
                showErrorToast({
                    titleId: "settings.tags.enable_tag.error.title",
                    descriptionId: "settings.tags.enable_tag.error.description",
                }, {
                    position: "bottom-center",
                });
                setEnableStates(function (prevState) {
                    var _a;
                    return (__assign(__assign({}, prevState), (_a = {}, _a[deal.id] = deal.isEnabled, _a)));
                });
            });
            return [2 /*return*/];
        });
    }); }, [dealAPI, showErrorToast, showSuccessToast]);
    var onDisableTag = useCallback(function (deal) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            setEnableStates(function (prevState) {
                var _a;
                return (__assign(__assign({}, prevState), (_a = {}, _a[deal.id] = false, _a)));
            });
            dealAPI
                .disableDeal(deal.id)
                .then(function () {
                showSuccessToast({
                    titleId: "settings.tags.disable_tag.success.title",
                }, {
                    position: "bottom-center",
                });
            })
                .catch(function (err) {
                console.error("Failed to disable tag", err);
                showErrorToast({
                    titleId: "settings.tags.disable_tag.error.title",
                    descriptionId: "settings.tags.disable_tag.error.description",
                }, {
                    position: "bottom-center",
                });
                setEnableStates(function (prevState) {
                    var _a;
                    return (__assign(__assign({}, prevState), (_a = {}, _a[deal.id] = deal.isEnabled, _a)));
                });
            });
            return [2 /*return*/];
        });
    }); }, [dealAPI, showErrorToast, showSuccessToast]);
    var onDeleteTag = useCallback(function (deal) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, dealAPI.deleteDeal(deal.id).then(function () {
                    refresh();
                })];
        });
    }); }, [dealAPI, refresh]);
    var _d = useMergeTagDialog(), mergeDialogProps = _d.mergeDialogProps, openMergeDialog = _d.openMergeDialog, closeMergeDialog = _d.closeMergeDialog;
    var _e = __read(useState(false), 2), isMerging = _e[0], setIsMerging = _e[1];
    var _f = __read(useState(null), 2), mergeItem = _f[0], setMergeItem = _f[1];
    var onMergeTag = useCallback(function (item) { return __awaiter(void 0, void 0, void 0, function () {
        var source, err_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setMergeItem(item);
                    return [4 /*yield*/, openMergeDialog()];
                case 1:
                    source = _a.sent();
                    if (!source) {
                        return [2 /*return*/];
                    }
                    setIsMerging(true);
                    _a.label = 2;
                case 2:
                    _a.trys.push([2, 4, 5, 6]);
                    return [4 /*yield*/, dealAPI.mergeDeal(item.id, source.id)];
                case 3:
                    _a.sent();
                    closeMergeDialog();
                    refresh();
                    return [3 /*break*/, 6];
                case 4:
                    err_1 = _a.sent();
                    console.error("Failed to merge tag", err_1);
                    showErrorToast({
                        titleId: "merge_tag.error.title",
                        descriptionId: "merge_tag.error.description",
                    }, {
                        position: "bottom-center",
                    });
                    return [3 /*break*/, 6];
                case 5:
                    setIsMerging(false);
                    return [7 /*endfinally*/];
                case 6: return [2 /*return*/];
            }
        });
    }); }, [openMergeDialog, dealAPI, closeMergeDialog, refresh, showErrorToast]);
    var onGoToTagDetail = useCallback(function (deal) {
        navigation.push("".concat(ROUTES.settings.tags.deal.group_detail, "/").concat(deal.id), {
            tagName: deal.name,
        });
    }, [navigation]);
    var renderContextMenu = useCallback(function (d) {
        var _a;
        return (React.createElement(TagContextMenu, { typeId: "settings.tags.title.deal", item: d, itemEnableState: (_a = enableStates[d.id]) !== null && _a !== void 0 ? _a : d.isEnabled, onEdit: onEditTag, onEnable: onEnableTag, onDisable: onDisableTag, onDelete: onDeleteTag, onMerge: onMergeTag }));
    }, [
        enableStates,
        onEditTag,
        onEnableTag,
        onDisableTag,
        onDeleteTag,
        onMergeTag,
    ]);
    var tableColumns = useMemo(function () { return [
        {
            titleId: "settings.tags.deal.detail.table.deal_tag",
            accessor: function (c) { return c.name; },
            render: function (_, c) {
                var _a;
                return (React.createElement(ColouredTag, { className: cn(!((_a = enableStates[c.id]) !== null && _a !== void 0 ? _a : c.isEnabled) && "opacity-50"), tag: c.name, colour: c.color }));
            },
        },
        {
            titleId: "settings.tags.deal.detail.table.full_name",
            accessor: function (c) { var _a; return (_a = c.fullName) !== null && _a !== void 0 ? _a : ""; },
        },
    ]; }, [enableStates]);
    var tableActions = useMemo(function () { return [
        {
            className: "flex items-center justify-end",
            render: renderContextMenu,
        },
    ]; }, [renderContextMenu]);
    return (React.createElement(React.Fragment, null,
        React.createElement(DefaultTagGroupDetail, { headerTitleId: "settings.tags.title.deal", headerTagCountId: "settings.tags.tag_counts.deal", searchPlaceholderId: "settings.tags.deal.detail.search_placeholder", addNewTagId: "settings.tags.deal.detail.add_new_deal", tableData: data, tableActions: tableActions, isInitializing: isInitializing, tableColumns: tableColumns, tagCount: totalCount, hasMoreData: hasMore, isFetchingMore: isFetchingMore, onTableItemClick: onGoToTagDetail, onFetchMore: nextPage, onGoBack: onGoBack, onAddNewClick: onAddNewDeal, onEditGroup: onEditGroup, onSearch: search, tagGroupSlug: TagGroupSlug.Deal }),
        mergeItem && (React.createElement(MergeTagDialog, __assign({}, mergeDialogProps, { destinationId: mergeItem.id, tagName: mergeItem.name, isMerging: isMerging, fetch: dealAPI.listDeals, keyExtractor: DealExtractors.id, labelExtractor: DealExtractors.name })))));
});
